// Alerts.jsx
import { IoIosInformationCircleOutline } from "react-icons/io";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import Axios from "axios";
import { BASEURL } from "../constants";
import {
  MessageSquare,
  User,
  Calendar,
  MessageCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// Loading Skeleton - matches ProgramSummary style
const LoadingSkeleton = () => (
  <div className="animate-pulse space-y-6">
    <div className="bg-white rounded-md p-8 border border-gray-200">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="h-7 w-32 bg-gray-200 rounded-full"></div>
        <div className="h-7 w-24 bg-gray-200 rounded-full"></div>
      </div>
      <div className="h-6 w-1/2 bg-gray-200 rounded mb-2"></div>
      <div className="h-4 w-1/3 bg-gray-200 rounded"></div>
    </div>
    <div className="bg-white rounded-xl p-6 border border-gray-200">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 bg-gray-200 rounded-lg"></div>
        <div>
          <div className="h-5 w-24 bg-gray-200 rounded mb-1"></div>
          <div className="h-3 w-16 bg-gray-200 rounded"></div>
        </div>
      </div>
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 w-48 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Empty State Component
const EmptyState = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md">
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
        <AlertCircle className="w-7 h-7 text-gray-400" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 mb-1">
        No Contact Logs
      </h3>
      <p className="text-sm text-gray-500 text-center max-w-md">
        Contact logs will appear here once available
      </p>
    </div>
  </div>
);

function Alerts({ forStudent = true, objectType = 5 }) {
  let { id } = useParams();
  const url = `${BASEURL}ContactLog?ObjectID=${
    forStudent ? localStorage.getItem("studentId") : id
  }&ObjectType=${objectType}&viewInLms=true`;

  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await Axios.get(url);
        setInfo(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (info.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-6">
      {/* Header Card - matches ProgramSummary style */}
      <div className="bg-white rounded-md border border-gray-200 p-8 shadow-md space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-full">
                <MessageSquare className="w-4 h-4" />
                Contact Logs
              </span>
              <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                {info.length} {info.length === 1 ? "entry" : "entries"}
              </span>
            </div>

            {/* Title */}
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Communication History
            </h2>

            {/* Subtitle */}
            <p className="text-sm text-gray-500">
              View all contact logs and messages
            </p>
          </div>
        </div>
      </div>

      {/* Contact Logs List */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Log Entries</h3>
            <p className="text-xs text-gray-500">Click to expand details</p>
          </div>
        </div>

        <div className="space-y-3">
          {info.map((item, key) => (
            <div
              key={key}
              className="border border-gray-100 rounded-lg overflow-hidden hover:border-gray-200 transition-colors"
            >
              {/* Main Row */}
              <div
                onClick={() => setExpandedId(expandedId === key ? null : key)}
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              >
                {/* Avatar */}
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-blue-600" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">
                      {item.username || "Unknown User"}
                    </h4>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {item.logTypeDesc && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                        {item.logTypeDesc}
                      </span>
                    )}
                    {item.nd && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        {item.nd}
                      </span>
                    )}
                  </div>
                </div>

                {/* Expand Icon */}
                <div className="flex-shrink-0">
                  {expandedId === key ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Expandable Message */}
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  expandedId === key ? "max-h-48" : "max-h-0"
                }`}
              >
                <div className="px-4 pb-4">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageCircle className="w-4 h-4 text-gray-500" />
                      <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                        Message
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {item.message || "No message provided"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Alerts;